import { Fragment, useMemo } from 'react';
import { convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';
import { ATTEMPT_TYPE_LABEL, type WorkOrderAttempt } from '@/types/workOrderAttempt.types';
import { ClipboardSignature } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EmailStatus, type EmailLogResponse } from '@/types/types';
import KeyValueListDisplay from '@/components/KeyValueDisplay';
import { EmailLogStatusLabel } from '@/utils/constant';

export interface AttemptAttachment {
  attachementId: string;
  url: string;
}

export const AttemptDetailsPeContent = ({
  attemptData,
  workOrderId,
  emailLogs,
  firstAttemptId,
  linkedToHomeInspection = false,
  canViewHiFirstAttempt = true,
}: {
  attemptData: WorkOrderAttempt[];
  workOrderId: string | undefined;
  emailLogs: EmailLogResponse[];
  /** Chronological first attempt id (createdAt ascending). */
  firstAttemptId?: string;
  /** Same linked signal as HI view (`linkedHIWorkOrderID` / enrichment). */
  linkedToHomeInspection?: boolean;
  canViewHiFirstAttempt?: boolean;
}) => {
  const navigate = useNavigate();

  const visibleAttempts = useMemo(
    () =>
      attemptData.filter((attempt) => {
        const isHiFirstAttemptView =
          linkedToHomeInspection && Boolean(firstAttemptId) && attempt.attemptId === firstAttemptId;
        return !(isHiFirstAttemptView && !canViewHiFirstAttempt);
      }),
    [attemptData, linkedToHomeInspection, firstAttemptId, canViewHiFirstAttempt]
  );

  return (
    <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm ">
      {emailLogs.length ? (
        <div className="flex flex-col gap-3 w-full">
          <span className="text-primary font-semibold">Emails Sent</span>
          <KeyValueListDisplay
            data={emailLogs.map((log: EmailLogResponse) => ({
              label: convertISODateToMMDDYYYY(log.sentAt) + ' ' + convertISOToTime(log.sentAt),
              value: EmailLogStatusLabel[log.status],
              valueClassName: log.status === EmailStatus.SENT ? 'text-green-700' : 'text-error',
            }))}
          />
        </div>
      ) : null}
      {visibleAttempts.map((attempt, index) => (
        <Fragment key={attempt.attemptId}>
          <div
            className="flex flex-row gap-2 justify-between p-4 rounded-lg bg-[#F5F5F5] cursor-pointer"
            onClick={() => navigate(`workorder/${workOrderId}/attempts?mode=view&attemptId=${attempt.attemptId}`)}
          >
            <div className="flex flex-row gap-2">
              <div className="flex align-top">
                <ClipboardSignature className="h-5 w-5 text-primary" />
              </div>
              <div className="flex flex-col gap-1 ">
                <span className="text-sm font-semibold">{`Attempt ${visibleAttempts.length - index}`}</span>
                <div className="flex flex-col gap-1 text-placeholder font-medium text-[12px]">
                  <span>{`Added: ${convertISODateToMMDDYYYY(attempt.createdAt)} ${convertISOToTime(attempt.createdAt)} by ${attempt.createdBy?.firstName} ${attempt.createdBy?.lastName}`}</span>
                  {attempt.updatedBy && (
                    <span>{`Updated: ${convertISODateToMMDDYYYY(attempt.updatedAt)} ${convertISOToTime(attempt.updatedAt)} by ${attempt.updatedBy?.firstName} ${attempt.updatedBy?.lastName}`}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-primary font-semibold ">
              <span>{ATTEMPT_TYPE_LABEL[attempt.type]}</span>
            </div>
          </div>
        </Fragment>
      ))}
      {visibleAttempts.length === 0 && <div className="w-full justify-center flex font-medium text-sm">No Updates</div>}
    </div>
  );
};
